// ===========================================
// Event Pricing Structures
// ===========================================
// A school's named, reusable bundles of ticket tiers — "Concert Pricing",
// "Holiday Camp Pricing" — selected per event in the editor.
//
// ⚠️ STRUCTURES SEED, THEY DO NOT BIND. Applying a structure copies its tiers into
// the event's own rows as starting values; editing a structure afterwards never
// retro-changes an event that already exists. That is a product decision, and the
// helper text under the tier editor is a DELIVERABLE, not a nicety — without it an
// admin editing "Concert Pricing" from $25 to $30 will reasonably assume their
// published concerts now charge $30.
//
// Modelled on AgeGroupsPage (the approved config-CRUD template): PageHeader +
// dnd-kit reorder + shared FormModal + ConfirmDialog, hide-inactive filter,
// skeleton loading, delete gated on inactive-first.

import { useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Edit as EditIcon,
  StarOutline as StarOutlineIcon,
  Star as StarIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PageHeader from '../../components/common/PageHeader';
import FormModal from '../../components/common/FormModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  useEventPricingStructures,
  useCreateEventPricingStructure,
  useUpdateEventPricingStructure,
  useDeleteEventPricingStructure,
  useReorderEventPricingStructures,
  useSetDefaultEventPricingStructure,
} from '../../hooks/useAdmin';
import { EventPricingStructure } from '../../api/admin.api';

// ===========================================
// TIER ROW (form state)
// ===========================================

/**
 * One tier as the form holds it.
 *
 * `key` is carried through for an EXISTING tier so a label edit keeps its
 * identity — the key is written onto every ticket ever sold under that tier, so
 * it must never change. A new row leaves it undefined and the server slugs one.
 *
 * `priceDollars` is a string because it is a text field, converted on submit.
 * There is deliberately no DATE here — only the `hasEndDate` flag: an event's early-bird cutoff is
 * specific to that event's date, so it is set on the event, not the structure.
 */
interface TierRow {
  /** Stable React key. NOT the tier key — purely for reconciliation. */
  rowId: string;
  key?: string;
  label: string;
  priceDollars: string;
  /**
   * Whether this KIND of tier stops selling on a day. A boolean, never a date:
   * the structure declares the shape ("Early Bird expires, Adult does not") and
   * the EVENT supplies the cutoff, because that day depends on the event's own
   * date. (Andrew, 2026-09-15.)
   */
  hasEndDate: boolean;
}

let rowSeq = 0;
const nextRowId = () => `row-${(rowSeq += 1)}`;

const emptyTier = (): TierRow => ({
  rowId: nextRowId(),
  label: '',
  priceDollars: '',
  hasEndDate: false,
});

/**
 * Dollars -> integer cents, or null when the text is not a usable price.
 *
 * ⚠️ Blank means "free" (0). Anything non-numeric or negative is INVALID, not 0 —
 * measured: `parseFloat('abc') * 100` is NaN, and NaN previously reached the API,
 * where the admin saw an opaque 400 from the backend validator rather than a
 * message against the field they typed in.
 */
function priceToCents(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return 0;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

// ===========================================
// SORTABLE ITEM
// ===========================================

interface SortableStructureItemProps {
  structure: EventPricingStructure;
  onEdit: (s: EventPricingStructure) => void;
  onDelete: (s: EventPricingStructure) => void;
  onSetDefault: (s: EventPricingStructure) => void;
}

function SortableStructureItem({
  structure,
  onEdit,
  onDelete,
  onSetDefault,
}: SortableStructureItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: structure.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Mirrors the server: delete is refused while active, and refused while default.
  const blockedReason = structure.isActive
    ? 'Set this structure to Inactive before deleting it'
    : structure.isDefault
      ? 'Make another structure the default before deleting this one'
      : null;
  const canDelete = blockedReason === null;

  const summary = structure.Tiers.length
    ? structure.Tiers.map((t) => {
        return `${t.label} ${formatCents(t.defaultPriceCents)}`;
      }).join(' · ')
    : 'No tiers yet';

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${structure.name}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          mr: 2,
          color: 'text.secondary',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIcon />
      </Box>

      <ListItemText
        primary={
          <Stack direction="row" spacing={1} alignItems="center">
            <span>{structure.name}</span>
            {structure.isDefault && <Chip label="Default" size="small" color="primary" />}
          </Stack>
        }
        secondary={summary}
        sx={{ flex: 1, pr: 2 }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {structure.isActive ? (
          <Chip label="Active" size="small" color="success" />
        ) : (
          <Chip label="Inactive" size="small" color="default" />
        )}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={structure.isDefault ? 'This is the default' : 'Make default'}>
            <span>
              <IconButton
                size="small"
                aria-label={structure.isDefault ? 'This is the default' : 'Make default'}
                onClick={() => !structure.isDefault && onSetDefault(structure)}
                disabled={structure.isDefault}
              >
                {structure.isDefault ? (
                  <StarIcon fontSize="small" color="primary" />
                ) : (
                  <StarOutlineIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" aria-label="Edit" onClick={() => onEdit(structure)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={blockedReason ?? 'Delete'}>
            <span>
              <IconButton
                size="small"
                aria-label={blockedReason ?? 'Delete'}
                onClick={() => canDelete && onDelete(structure)}
                color={canDelete ? 'error' : 'default'}
                disabled={!canDelete}
                sx={{ opacity: canDelete ? 1 : 0.5 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </ListItem>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function EventPricingStructuresPage() {
  const { data: structuresData, isLoading, error } = useEventPricingStructures();
  const createMutation = useCreateEventPricingStructure();
  const updateMutation = useUpdateEventPricingStructure();
  const deleteMutation = useDeleteEventPricingStructure();
  const reorderMutation = useReorderEventPricingStructures();
  const setDefaultMutation = useSetDefaultEventPricingStructure();

  const structures = structuresData ?? [];

  const [hideInactive, setHideInactive] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<EventPricingStructure | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isDefault: false,
    isActive: true,
  });
  const [tierRows, setTierRows] = useState<TierRow[]>([emptyTier()]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [toDelete, setToDelete] = useState<EventPricingStructure | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const filtered = useMemo(
    () => (hideInactive ? structures.filter((s) => s.isActive) : structures),
    [structures, hideInactive]
  );

  const handleAdd = () => {
    setEditing(null);
    setFormData({ name: '', description: '', isDefault: false, isActive: true });
    setTierRows([emptyTier()]);
    setModalOpen(true);
  };

  const handleEdit = (s: EventPricingStructure) => {
    setEditing(s);
    setFormData({
      name: s.name,
      description: s.description ?? '',
      isDefault: s.isDefault,
      isActive: s.isActive,
    });
    setTierRows(
      s.Tiers.length
        ? s.Tiers.map((t) => ({
            rowId: nextRowId(),
            key: t.key,
            label: t.label,
            priceDollars: t.defaultPriceCents ? String(t.defaultPriceCents / 100) : '',
            hasEndDate: t.hasEndDate ?? false,
          }))
        : [emptyTier()]
    );
    setModalOpen(true);
  };

  const handleDelete = (s: EventPricingStructure) => {
    setToDelete(s);
    setDeleteDialogOpen(true);
  };

  const handleSetDefault = async (s: EventPricingStructure) => {
    try {
      await setDefaultMutation.mutateAsync(s.id);
    } catch {
      // Error is surfaced by the mutation's onError (server message, verbatim).
    }
  };

  const updateTier = (index: number, patch: Partial<TierRow>) => {
    setTierRows((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const handleSubmit = async () => {
    try {
      const tiers = tierRows
        .filter((r) => r.label.trim() !== '')
        .map((r, index) => ({
          ...(r.key ? { key: r.key } : {}),
          label: r.label.trim(),
          defaultPriceCents: priceToCents(r.priceDollars) ?? 0,
          hasEndDate: r.hasEndDate,
          sortOrder: index,
        }));

      const data = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        isDefault: formData.isDefault,
        // Only the UPDATE schema accepts isActive; the CREATE schema has no such
        // field and validate() strips unknown keys (plain .parse, no .strict()),
        // so sending it on create silently did nothing. Send it only when editing.
        ...(editing ? { isActive: formData.isActive } : {}),
        tiers,
      };

      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
      setModalOpen(false);
    } catch {
      // Error is handled by the mutation
    }
  };

  const handleConfirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteMutation.mutateAsync(toDelete.id);
      setDeleteDialogOpen(false);
      setToDelete(null);
    } catch {
      // Error is handled by the mutation
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Reorder the FULL list, not the filtered one — otherwise hiding inactive
    // rows silently rewrites the order of the rows that are hidden.
    const ids = structures.map((s) => s.id);
    const reordered = arrayMove(
      ids,
      ids.indexOf(active.id as string),
      ids.indexOf(over.id as string)
    );

    try {
      await reorderMutation.mutateAsync(reordered);
    } catch {
      // Error is handled by the mutation
    }
  };

  const isFormLoading = createMutation.isPending || updateMutation.isPending;
  const namedRows = tierRows.filter((r) => r.label.trim() !== '');
  const hasInvalidPrice = namedRows.some((r) => priceToCents(r.priceDollars) === null);
  // Mirrors the server's uniqueTierLabels refine, which otherwise 400s with
  // "Each tier needs a distinct name" only after a round trip.
  const duplicateLabels = new Set(
    namedRows
      .map((r) => r.label.trim().toLowerCase())
      .filter((label, i, all) => all.indexOf(label) !== i),
  );
  const canSubmit =
    formData.name.trim() !== '' &&
    namedRows.length > 0 &&
    !hasInvalidPrice &&
    duplicateLabels.size === 0;

  return (
    <Box>
      <PageHeader
        title="Pricing Structures"
        subtitle="Reusable sets of ticket prices you can apply to an event. Drag to reorder."
        breadcrumbs={[{ label: 'Admin', path: '/admin' }, { label: 'Pricing Structures' }]}
        actionLabel="Add Structure"
        onAction={handleAdd}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load pricing structures. Please try again.
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <FormControlLabel
          control={
            <Switch
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
              size="small"
            />
          }
          label="Hide inactive structures"
        />
        {filtered.length !== structures.length && (
          <Chip
            label={`Showing ${filtered.length} of ${structures.length} structures`}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>

      <Paper sx={{ overflow: 'hidden' }}>
        {isLoading ? (
          <List>
            {[1, 2, 3].map((i) => (
              <ListItem key={i} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ mr: 2 }}>
                  <Skeleton variant="rectangular" width={24} height={24} />
                </Box>
                <ListItemText
                  primary={<Skeleton variant="text" width="40%" />}
                  secondary={<Skeleton variant="text" width="70%" />}
                />
              </ListItem>
            ))}
          </List>
        ) : error ? null : filtered.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {structures.length === 0
                ? 'No pricing structures yet. Add one to get started.'
                : 'No active pricing structures.'}
            </Typography>
          </Box>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filtered.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <List disablePadding>
                {filtered.map((s) => (
                  <SortableStructureItem
                    key={s.id}
                    structure={s}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onSetDefault={handleSetDefault}
                  />
                ))}
              </List>
            </SortableContext>
          </DndContext>
        )}
      </Paper>

      <FormModal
        open={modalOpen}
        title={editing ? 'Edit Pricing Structure' : 'Add Pricing Structure'}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        loading={isFormLoading}
        submitDisabled={!canSubmit}
        maxWidth="md"
      >
          <Stack spacing={3} sx={{ pt: 1 }}>
            <TextField
              label="Structure name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              placeholder="Concert Pricing"
            />

            <TextField
              label="Description (optional)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle2">Ticket tiers</Typography>
                <IconButton
                  size="small"
                  onClick={() => setTierRows((rows) => [...rows, emptyTier()])}
                  aria-label="Add tier"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Stack spacing={2}>
                {tierRows.map((row, index) => (
                  // Keyed by a STABLE rowId, never the array index. Today every
                  // field here is CONTROLLED, so key={index} is survivable —
                  // measured: mutating this back to key={index} left the suite
                  // 20/20 green. It is kept as a regression guard, because the
                  // moment one field goes uncontrolled (a DatePicker holding its
                  // own draft text is the likely one) index keys would carry a
                  // deleted row's state into its successor.
                  <Stack
                    key={row.rowId}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                    role="group"
                    aria-label={`Tier ${index + 1}`}
                  >
                    <TextField
                      label="Tier name"
                      value={row.label}
                      onChange={(e) => updateTier(index, { label: e.target.value })}
                      size="small"
                      sx={{ flex: 2 }}
                      placeholder="Early Bird"
                      error={duplicateLabels.has(row.label.trim().toLowerCase())}
                      helperText={
                        duplicateLabels.has(row.label.trim().toLowerCase())
                          ? 'Each tier needs a distinct name'
                          : undefined
                      }
                    />
                    <TextField
                      label="Price"
                      value={row.priceDollars}
                      onChange={(e) => updateTier(index, { priceDollars: e.target.value })}
                      size="small"
                      sx={{ flex: 1 }}
                      error={
                        row.label.trim() !== '' && priceToCents(row.priceDollars) === null
                      }
                      helperText={
                        row.label.trim() !== '' && priceToCents(row.priceDollars) === null
                          ? 'Enter a number, or leave blank for free'
                          : undefined
                      }
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                    <FormControlLabel
                      sx={{ flexShrink: 0, ml: { xs: 0, sm: 1 }, mt: { sm: 0.5 } }}
                      control={
                        <Switch
                          size="small"
                          checked={row.hasEndDate}
                          onChange={(e) => updateTier(index, { hasEndDate: e.target.checked })}
                          inputProps={{
                            // POSITIONAL, never the tier label - a name containing
                            // the label collides with that row's own fields under
                            // an unanchored getByLabelText(/<label>/i).
                            'aria-label': `Tier ${index + 1} ends on a date`,
                          }}
                        />
                      }
                      label="Ends on a date"
                    />
                    <Tooltip title="Remove tier">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Remove tier"
                          onClick={() =>
                            setTierRows((rows) => rows.filter((_, i) => i !== index))
                          }
                          disabled={tierRows.length === 1}
                          sx={{ mt: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                ))}
              </Stack>

              {/* ⚠️ DELIVERABLE, not a nicety. Structures SEED an event's prices;
                  they never bind them. Without this line an admin editing
                  "Concert Pricing" from $25 to $30 will reasonably expect their
                  already-published concerts to now charge $30 — and be wrong. */}
              <Alert severity="info" sx={{ mt: 2 }}>
                Changes apply to new events. Events already created keep their own prices.
              </Alert>
            </Box>

            <Stack direction="row" spacing={3}>
              {/* ⚠️ EDIT ONLY. createEventPricingStructureSchema has no isActive and
                  validate() strips unknown keys (plain .parse, no .strict()), so on
                  Add this control was a lie: pick "Inactive", get an Active
                  structure, no error. A new structure is always created active. */}
              {editing && (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="structure-status-label">Status</InputLabel>
                  <Select
                    labelId="structure-status-label"
                    label="Status"
                    value={formData.isActive ? 'active' : 'inactive'}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.value === 'active' })
                    }
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isDefault}
                    onChange={(e) =>
                      setFormData({ ...formData, isDefault: e.target.checked })
                    }
                  />
                }
                label="Use as the default for new events"
              />
            </Stack>
          </Stack>
      </FormModal>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete pricing structure?"
        message={
          toDelete
            ? `"${toDelete.name}" will be permanently deleted. Events created from it keep their own prices and are not affected.`
            : ''
        }
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setToDelete(null);
        }}
      />
    </Box>
  );
}
